import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2, Save, RotateCcw, Eraser } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  getWeeklyDishes,
  seedWeeklyDishesFromFallback,
  setWeeklyDishes,
  type WeeklyDish,
} from '@/lib/weeklyDishes'
import type { MenuItem } from '@/components/maia/types'
import { WeeklyDishForm } from './WeeklyDishForm'

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function WeeklyDishesManager() {
  const { t } = useTranslation('admin')
  const { t: tMaia } = useTranslation('maia')
  const [savedDishes, setSavedDishes] = useState<WeeklyDish[]>(() => getWeeklyDishes())
  const [draft, setDraft] = useState<WeeklyDish[]>(savedDishes)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedDish, setSelectedDish] = useState<WeeklyDish | null>(null)

  useEffect(() => {
    const fallback = {
      primis: tMaia('weeklyMenu.primis', { returnObjects: true }) as MenuItem[],
      seconds: tMaia('weeklyMenu.seconds', { returnObjects: true }) as MenuItem[],
    }
    seedWeeklyDishesFromFallback(fallback)
    const loaded = getWeeklyDishes()
    setSavedDishes(loaded)
    setDraft(loaded)
  }, [tMaia])

  const isDirty = useMemo(() => {
    return JSON.stringify(draft) !== JSON.stringify(savedDishes)
  }, [draft, savedDishes])

  const sortedDishes = useMemo(() => {
    return [...draft].sort((a, b) => b.createdAt - a.createdAt)
  }, [draft])

  const handleSaveChanges = () => {
    setWeeklyDishes(draft)
    const updated = getWeeklyDishes()
    setSavedDishes(updated)
    setDraft(updated)
  }

  const handleDiscardChanges = () => {
    setDraft(savedDishes)
  }

  const handleAddToDraft = (
    data: Omit<WeeklyDish, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }
  ) => {
    if (data.id) {
      setDraft((prev) =>
        prev.map((d) =>
          d.id === data.id
            ? { ...d, ...data, updatedAt: Date.now() }
            : d
        )
      )
    } else {
      const created: WeeklyDish = {
        ...data,
        id: generateId(),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      setDraft((prev) => [created, ...prev])
    }
  }

  const handleAdd = () => {
    setSelectedDish(null)
    setIsModalOpen(true)
  }

  const handleEdit = (dish: WeeklyDish) => {
    setSelectedDish(dish)
    setIsModalOpen(true)
  }

  const handleDelete = (id: string) => {
    setDraft((prev) => prev.filter((d) => d.id !== id))
  }

  const handleClear = () => {
    setDraft([])
  }

  const handleClose = () => {
    setIsModalOpen(false)
    setSelectedDish(null)
  }

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-cord bg-white">
        <header className="flex flex-wrap items-center justify-between gap-3 bg-maia-soft px-5 py-4">
          <h3 className="font-display text-lg font-bold text-ink">
            {t('zone.weeklyDishes.title')}
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={!isDirty}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors',
                isDirty
                  ? 'bg-maia text-white shadow-sm hover:bg-maia-dark'
                  : 'cursor-not-allowed border border-dashed border-muted/40 bg-transparent text-muted/60'
              )}
            >
              <Save className="h-3.5 w-3.5" aria-hidden="true" />
              {t('zone.weeklyDishes.saveChanges')}
            </button>
            <button
              type="button"
              onClick={handleDiscardChanges}
              disabled={!isDirty}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors',
                isDirty
                  ? 'bg-white text-ink shadow-sm hover:bg-cream'
                  : 'cursor-not-allowed border border-dashed border-muted/40 bg-transparent text-muted/60'
              )}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              {t('zone.weeklyDishes.discardChanges')}
            </button>
            <button
              type="button"
              onClick={handleClear}
              disabled={draft.length === 0}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors',
                draft.length === 0
                  ? 'cursor-not-allowed border border-dashed border-muted/40 bg-transparent text-muted/60'
                  : 'bg-pizza text-white shadow-sm hover:bg-pizza-dark'
              )}
            >
              <Eraser className="h-3.5 w-3.5" aria-hidden="true" />
              {t('zone.weeklyDishes.clearDishes')}
            </button>
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-1.5 rounded-full bg-maia px-3 py-1.5 text-xs font-bold text-white shadow-sm transition-colors hover:bg-maia-dark"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              {t('zone.weeklyDishes.addDish')}
            </button>
          </div>
        </header>

        {sortedDishes.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <p className="text-sm text-muted">{t('zone.weeklyDishes.empty')}</p>
          </div>
        ) : (
          <>
            <div className="sm:hidden divide-y divide-cord">
              {sortedDishes.map((dish) => (
                <div
                  key={dish.id}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  {dish.image ? (
                    <img
                      src={dish.image}
                      alt={dish.title}
                      className="h-14 w-14 rounded-lg object-cover"
                    />
                  ) : (
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-lg bg-cord text-[10px] font-medium text-muted">
                      {t('zone.weeklyDishes.table.noPhoto')}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{dish.title}</p>
                    <p className="text-sm text-muted">{dish.price || '-'}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleEdit(dish)}
                      aria-label={t('zone.weeklyDishes.editDish')}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink/10 text-ink transition-colors hover:bg-maia hover:text-white"
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(dish.id)}
                      aria-label={t('zone.weeklyDishes.delete')}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink/10 text-ink transition-colors hover:bg-pizza hover:text-white"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-cord text-xs uppercase tracking-wider text-muted">
                  <th className="py-3 pl-5 pr-4 font-semibold">
                    {t('zone.weeklyDishes.table.photo')}
                  </th>
                  <th className="py-3 pr-4 font-semibold">
                    {t('zone.weeklyDishes.table.type')}
                  </th>
                  <th className="py-3 pr-4 font-semibold">
                    {t('zone.weeklyDishes.table.title')}
                  </th>
                  <th className="py-3 pr-4 font-semibold">
                    {t('zone.weeklyDishes.table.description')}
                  </th>
                  <th className="py-3 pr-4 font-semibold">
                    {t('zone.weeklyDishes.table.allergens')}
                  </th>
                  <th className="py-3 pr-4 font-semibold">
                    {t('zone.weeklyDishes.table.price')}
                  </th>
                  <th className="py-3 pr-5 font-semibold">
                    {t('zone.weeklyDishes.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cord">
                {sortedDishes.map((dish) => (
                  <tr
                    key={dish.id}
                    className="transition-colors hover:bg-cream/60"
                  >
                    <td className="py-3 pl-5 pr-4">
                      {dish.image ? (
                        <img
                          src={dish.image}
                          alt={dish.title}
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                      ) : (
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-cord text-[10px] font-medium text-muted">
                          {t('zone.weeklyDishes.table.noPhoto')}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                          dish.type === 'primo'
                            ? 'bg-maia/15 text-maia-dark'
                            : 'bg-bar/15 text-ink'
                        )}
                      >
                        {dish.type === 'primo'
                          ? t('zone.weeklyDishes.typePrimo')
                          : t('zone.weeklyDishes.typeSecondo')}
                      </span>
                    </td>
                    <td className="py-3 pr-4 font-medium text-ink">
                      {dish.title}
                    </td>
                    <td className="max-w-xs py-3 pr-4">
                      <p className="truncate text-ink/80">{dish.description || '-'}</p>
                    </td>
                    <td className="py-3 pr-4 text-ink/80">
                      {dish.allergens || '-'}
                    </td>
                    <td className="py-3 pr-4 font-medium text-ink">
                      {dish.price || '-'}
                    </td>
                    <td className="py-3 pr-5">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(dish)}
                          aria-label={t('zone.weeklyDishes.editDish')}
                          className="inline-flex items-center rounded-full bg-ink/10 p-1.5 text-ink transition-colors hover:bg-maia hover:text-white"
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(dish.id)}
                          aria-label={t('zone.weeklyDishes.delete')}
                          className="inline-flex items-center rounded-full bg-ink/10 p-1.5 text-ink transition-colors hover:bg-pizza hover:text-white"
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <WeeklyDishForm
        isOpen={isModalOpen}
        initialDish={selectedDish}
        onClose={handleClose}
        onSave={handleAddToDraft}
      />
    </>
  )
}
